// 1. **Google Map Integration**: The component integrates Google Maps using the `@react-google-maps/api` library, rendering a map with markers based on store data. The map center is dynamically updated based on the selected store or a search query.

// 2. **Search Functionality**: Users can search for stores by town, postcode, or store name using a search input field. The map updates with the location of the store matching the search query.

// 3. **Dynamic Marker and InfoWindow**: Markers are displayed on the map for each store. When a marker is clicked, an `InfoWindow` opens with detailed store information, such as the store's name, address, phone number, and email.

// 4. **Handling State and Props**: The component manages several states, including the map center, search input, and a flag indicating if the map has loaded. It also receives `storeData`, `selectedPlace`, and `handleSelectedPlace` as props to control the store's location and details.

// 5. **Conditional Rendering**: The map and its markers are conditionally rendered only on the client side (`isClient` state). The component also uses a flag to ensure the map is loaded before enabling certain functionalities like search and store selection.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, LoadScript, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import Button from 'components/commercetools-ui/atoms/button';
import { useSearchParams } from 'next/navigation';
import { store } from '../../../../../types/product/store';

type selectedPlace = {
  name: string;
  location: {
    lat: number;
    lng: number;
  };
};

type storeData = {
  id: string;
  version: number;
  versionModifiedAt: string;
  createdAt: string;
  lastModifiedAt: string;
  lastModifiedBy: {
    isPlatformClient: boolean;
    user: {
      typeId: string;
      id: string;
    };
  };
  createdBy: {
    clientId: string;
    isPlatformClient: boolean;
  };
  key: string;
  roles: string[];
  name: {
    'en-GB': string;
  };
  custom: {
    type: {
      typeId: string;
      id: string;
    };
    fields: {
      isEnabled: boolean;
      channelType: string;
      googlePlaceID: string;
    };
  };
  address: {
    streetName: string;
    streetNumber: string;
    postalCode: string;
    city: string;
    region: string;
    state: string;
    country: string;
    phone: string;
    email: string;
  };
  geoLocation: {
    coordinates: [number, number];
    type: string;
  };
};

export type props = {
  storeData: storeData[];
  selectedPlace: selectedPlace | undefined;
  handleSelectedPlace: (place: selectedPlace) => void;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUserLocation: React.Dispatch<
    React.SetStateAction<{
      lat: number;
      lng: number;
    }>
  >;
  userLocation: {
    lat: number;
    lng: number;
  };
};

const NeighborhoodDiscovery = ({
  storeData,
  selectedPlace,
  handleSelectedPlace,
  isOpen,
  setIsOpen,
  setUserLocation,
}: Omit<props, 'userLocation'>) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const handleMapLoad = () => {
    setMapLoaded(true);
  };
  const searchParams = useSearchParams();
  const query = searchParams.get('q');

  const [mapCenter, setMapCenter] = useState({
    lat: storeData?.[0]?.geoLocation?.coordinates?.[1],
    lng: storeData?.[0]?.geoLocation?.coordinates?.[0],
  });
  const [isClient, setIsClient] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const queryPlace = storeData?.find((store: store) => store?.name?.['en-GB'].toLowerCase() === query);
  const infoWindowRef = useRef(null);
  let store: store | undefined;

  const scaledSize = window.google?.maps?.Size ? new window.google.maps.Size(32, 32) : undefined;

  const handleMarkerClick = (store: store) => {
    handleSelectedPlace({
      name: store?.name?.['en-GB'],
      location: {
        lat: store?.geoLocation?.coordinates?.[1],
        lng: store?.geoLocation?.coordinates?.[0],
      },
    });
    setIsOpen(true);
  };

  const handleCloseClick = () => {
    setIsOpen(false);
  };
  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (mapLoaded && query && queryPlace) {
      setSearchInput(query);
      handleSelectedPlace({
        name: queryPlace?.name?.['en-GB'],
        location: {
          lat: queryPlace?.geoLocation?.coordinates?.[1],
          lng: queryPlace?.geoLocation?.coordinates?.[0],
        },
      });
    }
  }, [mapLoaded, query, queryPlace]);
  useEffect(() => {
    // Effect to update map center
    if (selectedPlace) {
      setSearchInput(selectedPlace?.name);
      setMapCenter(selectedPlace?.location);
    } else if (storeData?.length > 0) {
      setMapCenter({
        lat: storeData?.[0]?.geoLocation?.coordinates?.[1],
        lng: storeData?.[0]?.geoLocation?.coordinates?.[0],
      });
    }
  }, [selectedPlace, storeData]);

  const handleSearch = async (e: any) => {
    e.preventDefault();
    store = storeData?.find(
      (neighborhood: store) =>
        neighborhood?.name?.['en-GB']?.toLowerCase()?.includes(searchInput?.toLowerCase()) ||
        neighborhood?.address?.city?.toLowerCase()?.includes(searchInput?.toLowerCase()) ||
        neighborhood?.address?.postalCode?.toLowerCase()?.includes(searchInput?.toLowerCase()),
    );

    if (store) {
      setMapCenter({
        lat: store?.geoLocation?.coordinates?.[1],
        lng: store?.geoLocation?.coordinates?.[0],
      });
      handleSelectedPlace({
        name: store?.name?.['en-GB'],
        location: {
          lat: store?.geoLocation?.coordinates?.[1],
          lng: store?.geoLocation?.coordinates?.[0],
        },
      });
    }
  };

  const handleLocationClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setMapCenter({ lat: latitude, lng: longitude });
        },
        (error) => {
          console.error('Geolocation error', error);
        },
      );
    } else {
      console.error('Geolocation is not supported by this browser.');
    }
  };

  if (!isClient) {
    return <></>;
  }

  const infoDetails = storeData?.filter((store: store) => store?.name?.['en-GB'] === selectedPlace?.name);

  return (
    <LoadScript googleMapsApiKey="AIzaSyCt6kFyLYkw0mPD7XQUEWhLskhNNB6Mpks" libraries={['places']}>
      <div style={{ position: 'relative' }}>
        <div className="mt-20 flex w-full max-w-md justify-center pt-10">
          <form onSubmit={handleSearch} className="flex w-full items-center">
            <div className="relative flex-grow">
              <MagnifyingGlassIcon className="absolute -top-41 left-58 z-30 h-23 w-23 text-gray-500" />
              <input
                type="text"
                className="absolute -top-49 left-54 z-20  w-full rounded-md border border-gray-300 pl-30 pr-10 focus:outline-none"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Town or Postcode"
              />
              <div className="absolute -right-54 -top-48 z-20">
                <Button type="submit" className="rounded-l-none font-semibold uppercase" variant="primary">
                  Find
                </Button>
              </div>

              <img
                src="https://www.americangolf.co.uk/on/demandware.static/Sites-AmericanGolf-GB-Site/-/default/dwc1f4f32f/images/interface/vector.svg"
                alt="Location Icon"
                className="absolute -top-40 right-35 z-20 h-23 w-23  cursor-pointer"
                onClick={handleLocationClick}
              />
            </div>
          </form>
        </div>

        {isClient && (
          <GoogleMap
            mapContainerStyle={{ height: '500px', width: '900px' }}
            center={mapCenter}
            zoom={5}
            onLoad={(map) => {
              if (window?.google?.maps) {
                const bounds = new window.google.maps.LatLngBounds(mapCenter);
                storeData.forEach((store) =>
                  bounds.extend(
                    new window.google.maps.LatLng(
                      store?.geoLocation?.coordinates?.[1],
                      store?.geoLocation?.coordinates?.[0],
                    ),
                  ),
                );
                map.fitBounds(bounds);
              }
              handleMapLoad();
            }}
          >
            {storeData?.map((neighborhood: store) => (
              <MarkerF
                key={neighborhood?.id}
                icon={{
                  url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                  scaledSize: scaledSize,
                }}
                position={{
                  lat: neighborhood?.geoLocation?.coordinates?.[1],
                  lng: neighborhood?.geoLocation?.coordinates?.[0],
                }}
                title={neighborhood?.name?.['en-GB']}
                onClick={() => handleMarkerClick(neighborhood)}
              />
            ))}

            {isOpen && (
              <InfoWindowF
                position={selectedPlace?.location}
                onCloseClick={handleCloseClick}
                options={{ pixelOffset: new window.google.maps.Size(0, -30) }} // Position above the marker
                ref={infoWindowRef}
              >
                <div className="h-auto w-[350px] p-10 ">
                  <p className="text-20">{infoDetails?.[0]?.name?.['en-GB']}</p>
                  <p className="mt-2">{infoDetails?.[0]?.address?.streetNumber}</p>
                  <p className="pb-16">{infoDetails?.[0]?.address?.city}</p>
                  <hr></hr>
                  <div className="mt-10 flex flex-row">
                    <p className="w-20">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                        <g id="SVGRepo_iconCarrier">
                          {' '}
                          <path
                            d="M3 5.5C3 14.0604 9.93959 21 18.5 21C18.8862 21 19.2691 20.9859 19.6483 20.9581C20.0834 20.9262 20.3009 20.9103 20.499 20.7963C20.663 20.7019 20.8185 20.5345 20.9007 20.364C21 20.1582 21 19.9181 21 19.438V16.6207C21 16.2169 21 16.015 20.9335 15.842C20.8749 15.6891 20.7795 15.553 20.6559 15.4456C20.516 15.324 20.3262 15.255 19.9468 15.117L16.74 13.9509C16.2985 13.7904 16.0777 13.7101 15.8683 13.7237C15.6836 13.7357 15.5059 13.7988 15.3549 13.9058C15.1837 14.0271 15.0629 14.2285 14.8212 14.6314L14 16C11.3501 14.7999 9.2019 12.6489 8 10L9.36863 9.17882C9.77145 8.93713 9.97286 8.81628 10.0942 8.64506C10.2012 8.49408 10.2643 8.31637 10.2763 8.1317C10.2899 7.92227 10.2096 7.70153 10.0491 7.26005L8.88299 4.05321C8.745 3.67376 8.67601 3.48403 8.55442 3.3441C8.44701 3.22049 8.31089 3.12515 8.15802 3.06645C7.98496 3 7.78308 3 7.37932 3H4.56201C4.08188 3 3.84181 3 3.63598 3.09925C3.4655 3.18146 3.29814 3.33701 3.2037 3.50103C3.08968 3.69907 3.07375 3.91662 3.04189 4.35173C3.01413 4.73086 3 5.11378 3 5.5Z"
                            stroke="#000000"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          ></path>{' '}
                        </g>
                      </svg>
                    </p>
                    <p className="ml-5 text-15">{infoDetails?.[0]?.address?.phone}</p>
                  </div>
                  <div className="mt-10 flex flex-row">
                    <p className="w-20">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
                        <g id="SVGRepo_iconCarrier">
                          {' '}
                          <path
                            d="M4 7.00005L10.2 11.65C11.2667 12.45 12.7333 12.45 13.8 11.65L20 7"
                            stroke="#000000"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          ></path>{' '}
                          <rect
                            x="3"
                            y="5"
                            width="18"
                            height="14"
                            rx="2"
                            stroke="#000000"
                            stroke-width="2"
                            stroke-linecap="round"
                          ></rect>{' '}
                        </g>
                      </svg>
                    </p>
                    <p className="ml-5 text-15">{infoDetails?.[0]?.address?.email}</p>
                  </div>
                  <div className="flex-col justify-center">
                    <button className="my-10 rounded-sm  bg-[#1dce8f] p-10">VIEW STORE FOR MORE INFO</button>
                    <button className="my-8 rounded-sm bg-[#030303] p-10 text-white">VIEW STORE FOR MORE INFO</button>
                    <button className="my-8 rounded-sm  bg-[#030303] p-10 text-white">VIEW STORE FOR MORE INFO</button>
                  </div>
                </div>
              </InfoWindowF>
            )}
          </GoogleMap>
        )}
      </div>
    </LoadScript>
  );
};

export default NeighborhoodDiscovery;
